import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { optionsUI } from 'src/app/utils/optionsUI';
import { Route } from 'src/app/_models/view/route';
import { RouteService } from 'src/app/services/route.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { RouteOptionComponent } from '../../Routeoptions/routeoption.component';
import { HexToRGBaPipe } from 'src/app/_helpers/hex-to-rgba.pipe';
import { SaveRouteComponent } from '../saved-route/save-route.component';
import { UpdateRouteComponent } from '../saved-route/update-route.component';
import { DateTime } from 'luxon';
import { RouteEditMode } from 'src/app/_models/state/routeState';
import { BrazeService } from 'src/app/services/braze.service';
import { ModalboxComponent } from '../../modalbox/modalbox.component';
import { SubscriptionManager } from 'src/app/_helpers/subscription-manager';
import { AuthenticationService } from 'src/app/auth/services/authentication.service';
import { Common } from '@trimblemaps/trimblemaps-js';
import { ApplicationSettingsService } from 'src/app/services/application-settings.service';
import { combineLatest, Observable } from 'rxjs';
import {
  CurrencyState,
  NumericKeyValueState,
} from 'src/app/_models/state/optionUIState';
import { map } from 'rxjs/operators';
import { SubscriptionLevel } from 'src/app/auth/models/user';
import { GoogleAnalytics } from 'src/app/services/google-analytics.service';
import { RouteImportModalComponent } from '../route-import-modal/route-import-modal.component';
import { RoutingProfileService } from 'src/app/services/vehicle-routing-profile.service';
import { RoutingProfileModel } from 'src/app/_models/profilews/profilews';
import { ConversionService } from 'src/app/services/conversion.service';
import { AlertService } from 'src/app/auth/services/alert.service';
import { DataVersionService } from 'src/app/services/data-version.service';

@Component({
  selector: 'app-route',
  templateUrl: './route.component.html',
  styleUrls: ['./route.component.scss'],
  providers: [HexToRGBaPipe],
  encapsulation: ViewEncapsulation.None,
})
export class RouteComponent implements OnInit, OnDestroy, AfterViewChecked {
  @HostBinding('class') cssClass = 'card shadow mb-3';
  @Input() route: Route;

  subscriptionManager = new SubscriptionManager();
  // Enum references for template use
  routeEditModeEnum = RouteEditMode;
  optionsUI = optionsUI;
  profile: RoutingProfileModel;
  isLoading$: Observable<boolean>;
  displayCurrency$: Observable<CurrencyState>;
  displayDistUnit$: Observable<string>;
  isEuRegion$: Observable<boolean>;
  routeCount$: Observable<number>;
  isOpen = false;
  hasPremiumSubscription: boolean;
  profileDetails: string;
  isPremium$: Observable<boolean>;
  precision$: Observable<number>;
  isOldVersion = false;
  // Create Profile
  readonly baseAddEditModalClasses = 'modal-dialog-centered modal-xl';
  dataVersions: NumericKeyValueState[] = [];
  // TODO this component is too large and needs to be broken down into smaller components

  constructor(
    private brazeService: BrazeService,
    private routeService: RouteService,
    private modalService: BsModalService,
    private profileService: RoutingProfileService,
    private authenticationService: AuthenticationService,
    private applicationSettingsService: ApplicationSettingsService,
    private googleAnalytics: GoogleAnalytics,
    private conversionService: ConversionService,
    private alertService: AlertService,
    private dataversionService: DataVersionService,
    private cdRef: ChangeDetectorRef
  ) {}
  ngAfterViewChecked(): void {
    this.cdRef.detectChanges();
  }

  _isOldDataVersion(historicalDataVersion: string): boolean {
    let isOld = false;
    if (historicalDataVersion.startsWith('PCM')) {
      const versionNumStr = historicalDataVersion.substring(3);
      const versionNum = parseInt(versionNumStr);
      if (versionNum < 28) {
        isOld = true;
      }
    }
    return isOld;
  }

  // Actions
  ngOnInit(): void {
    this.precision$ = this.applicationSettingsService.searchView$.pipe(
      map((searchOption) => searchOption?.precision)
    );
    this.isOldVersion = this._isOldDataVersion(
      this.route.historicalDataVersion
    );
    // if (this.isOldVersion) {
    //   this.route.historicalDataVersion = 'current';
    // }
    this.routeCount$ = this.routeService.routesCount$;
    const dateTime = DateTime.now().toLocal();
    this.isLoading$ = this.routeService.isLoading$;
    const routeStore = this.routeService.getStorage();

    const routeState = routeStore?.routeList?.find(
      (r) => Number(r?.detailReport?.RouteID) === this.route.id
    );
    this.dataVersions = this.dataversionService.getDataVersionList();
    this.displayDistUnit$ =
      this.applicationSettingsService.selectedDistanceUnit$.pipe(
        map(
          (distUnit) =>
            this.optionsUI.distanceUnits[
              typeof routeState?.optionsState?.distanceUnits !== 'undefined'
                ? routeState?.optionsState.distanceUnits
                : distUnit
            ]?.symbol
        )
      );
    this.isEuRegion$ = this.authenticationService.currentUser.pipe(
      map((userInfo) => userInfo?.region.toLowerCase() === Common.Region.EU)
    );
    this.isPremium$ = this.authenticationService.currentUser.pipe(
      map(
        (userInfo) => userInfo?.subscriptionLevel === SubscriptionLevel.PREMIUM
      )
    );
    if (this.route.isSelected) {
      this.profile = this.profileService.getProfiles().find((p) => {
        return p.routingProfileId === this.route.profileId;
      });
    }
    this.displayCurrency$ = combineLatest([
      this.authenticationService.currentUser,
      this.applicationSettingsService.store$,
    ]).pipe(
      map(([userInfo, appData]) => {
        this.getRouteOptionsDetails(appData, routeState);
        this.hasPremiumSubscription = userInfo
          ? userInfo.subscriptionLevel === SubscriptionLevel.PREMIUM
          : false;
        const tollCurrency =
          userInfo?.region.toLowerCase() === Common.Region.EU
            ? this.optionsUI.tollCurrencyEU
            : this.optionsUI.tollCurrency;
        return tollCurrency.find(
          (currencyData) =>
            currencyData?.value ===
            (typeof routeState?.optionsState?.tollCurrency !== 'undefined'
              ? routeState?.optionsState.tollCurrency
              : appData?.tollCurrency)
        );
      })
    );
  }
  getRouteOptionsDetails(data, routeState) {
    this.profileDetails = this.conversionService.getRoutingProfileSummary(
      this.profile,
      data,
      routeState
    );
  }
  // Save or updae route
  saveRoute() {
    if (this.route.savedId !== null) {
      this.modalService.show(UpdateRouteComponent, {
        class: 'modal-sm save-modal',
        backdrop: 'static',
        initialState: { route: this.route },
      });
    } else {
      this.modalService.show(SaveRouteComponent, {
        class: 'modal-sm save-modal',
        backdrop: 'static',
        initialState: { route: this.route },
      });
    }
    this.googleAnalytics.addGA(
      'Save_Route',
      'SaveRoute_click',
      'Route',
      'save route clicked'
    );
  }

  deleteRoute() {
    this.googleAnalytics.addGA(
      'Delete_Route',
      'DeleteRoute_click',
      'Route',
      'delete route clicked'
    );
    const initialState = {
      title: 'Delete Route',
      list: [
        `Are you sure you want to delete <strong>${this.route.name}</strong>?`,
      ],
      actionButtonText: 'Delete',
      id: this.route.id,
      btnClass: 'btn btn-danger',
    };
    const dialogRef = this.modalService.show(ModalboxComponent, {
      initialState,
      ignoreBackdropClick: true,
      keyboard: false,
      animated: true,
      class: 'modal-dialog-centered',
    });

    this.subscriptionManager.manage = dialogRef.content.event.subscribe(
      (id: number) => {
        this.routeService.deleteRoute(id);
        this.alertService.clear();
      }
    );
  }

  duplicateRoute() {
    this.brazeService.track('Clicks Duplicate Route');
    this.googleAnalytics.addGA(
      'Duplicate_Route',
      'DuplicateRoute_click',
      'Route',
      'duplicate route clicked'
    );
    this.routeService.duplicateRoute(this.route.id);
  }

  frameRoute() {
    this.routeService.frameRouteOnMap(this.route.id);
  }

  toggleVisibilityOnMap(event: PointerEvent) {
    const target = event.target as HTMLInputElement;
    this.routeService.setVisibilityOnMap(this.route.id, target.checked);
    this.googleAnalytics.addGA(
      target.checked ? 'Show_Route' : 'Hide_Route',
      'toggleRouteVisibility',
      'Route',
      'toggle route clicked'
    );
  }

  showStopOptions() {
    this.googleAnalytics.addGA(
      'Stop_Time',
      'stopTime_click',
      'Route',
      'stop time clicked'
    );
    this.routeService.setRouteEditMode(
      this.route.id,
      RouteEditMode.EditStopOptions
    );
  }

  // UI Helpers

  showRouteOptions() {
    const modalRef = this.modalService.show(RouteOptionComponent, {
      class: 'modal-lg',
      backdrop: 'static',
    });
  }

  // For a selected route, collapse or expand the panel.
  // For a not selcted route, select the route and expand the panel.
  expandRoutePanel() {
    this.alertService.clear();
    this.googleAnalytics.addGA(
      'Select_Route',
      'Route_Panel_click',
      'Route',
      'route panel clicked'
    );
    this.routeService.setRoutePanelExpandable(this.route.id);
  }

  openImportUsersModal() {
    this.modalService.show(RouteImportModalComponent, {
      class: 'modal-xl assets-import-modal',
      backdrop: 'static',
      initialState: { route: this.route },
    });
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }
}
